/**
 * F09 v2 (E14 / §14 "Integrations") — the F43 user page's verification card.
 *
 * Fed by `GET /admin/verification-entities?ownerUserId=`: one row per entity
 * the user owns or is (player, trainer, owned venues, club/organizer accounts)
 * with its tick, owner-state badge and deep links to
 * `/verifications/entities/:type/:id` and to its open request.
 *
 * Read needs `verifications:read`; without it the card falls back to the two
 * booleans on the user payload (`playerVerified` / `trainerVerified`). The
 * direct-verify action is shown only with `verifications:issue_privileged`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, Card, Skeleton } from '@bravemove/ui-web';
import type { EntityVerificationSummary } from '@bravemove/types';
import { adminVerificationApi } from '../../api/verification';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import { InertTick, VerificationStateCell } from './EntityVerificationCard';
import {
  VERIFICATION_LINK_CLASS,
  directVerifyPath,
  entityLabel,
  entityPath,
  requestPath,
} from './labels';

interface LoadState {
  readonly key: string;
  readonly rows: readonly EntityVerificationSummary[] | null;
  readonly failed: boolean;
}

export interface UserVerificationsCardProps {
  readonly userId: string;
  /** Fallback ticks from the user payload (shown without `verifications:read`). */
  readonly playerVerified: boolean | undefined;
  readonly trainerVerified: boolean | undefined;
}

export function UserVerificationsCard({
  userId,
  playerVerified,
  trainerVerified,
}: UserVerificationsCardProps) {
  const { t } = useI18n();
  const { canRead, canIssue } = useVerificationPermissions();
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState>({ key: '', rows: null, failed: false });
  const key = `${userId}:${reloadToken}`;

  useEffect(() => {
    if (!canRead || !userId) return;
    let alive = true;
    adminVerificationApi
      .entitiesForOwner(userId)
      .then((rows) => {
        if (alive) setState({ key, rows, failed: false });
      })
      .catch(() => {
        if (alive) setState({ key, rows: null, failed: true });
      });
    return () => {
      alive = false;
    };
  }, [canRead, userId, key]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  const current = state.key === key ? state : null;

  return (
    <Card data-testid="user-verifications-card">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-[13px] font-semibold text-ink">
              {t('admin:verification.integrations.userCardTitle')}
            </h3>
            <p className="text-[12px] text-ink-3">
              {t('admin:verification.integrations.userCardSubtitle')}
            </p>
          </div>
          {canIssue ? (
            <Link
              to={directVerifyPath('player', userId)}
              className={VERIFICATION_LINK_CLASS}
              data-testid="user-verifications-direct-verify"
            >
              {t('admin:verification.entity.directVerify')}
            </Link>
          ) : null}
        </div>

        {!canRead ? (
          <ul className="space-y-1.5" data-testid="user-verifications-fallback">
            <FallbackRow
              label={entityLabel(t, 'player')}
              verified={playerVerified}
              entityType="player"
            />
            <FallbackRow
              label={entityLabel(t, 'trainer')}
              verified={trainerVerified}
              entityType="trainer"
            />
          </ul>
        ) : current === null ? (
          <Skeleton className="h-16 w-full rounded-lg" />
        ) : current.failed ? (
          <div className="space-y-2" data-testid="user-verifications-error">
            <Banner tone="danger">{t('admin:verification.common.loadError')}</Banner>
            <Button variant="secondary" size="xs" onClick={reload}>
              {t('admin:verification.common.retry')}
            </Button>
          </div>
        ) : current.rows === null || current.rows.length === 0 ? (
          <p className="text-[12.5px] text-ink-3" data-testid="user-verifications-empty">
            {t('admin:verification.integrations.userCardEmpty')}
          </p>
        ) : (
          <ul className="divide-y divide-outline" data-testid="user-verifications-list">
            {current.rows.map((row) => (
              <EntityRow key={`${row.entityType}:${row.entityId}`} row={row} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function EntityRow({ row }: { readonly row: EntityVerificationSummary }) {
  const { t } = useI18n();
  const testId = `user-verification-${row.entityType}-${row.entityId}`;
  return (
    <li className="flex flex-wrap items-center gap-2 py-2.5" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-ink">{row.entityName}</p>
        <p className="text-[11px] uppercase tracking-wide text-ink-3">
          {entityLabel(t, row.entityType)}
        </p>
      </div>
      <VerificationStateCell
        entityType={row.entityType}
        state={row.verificationState}
        verified={row.verificationStatus === 'verified'}
        openRequest={row.openRequest}
      />
      <div className="flex flex-wrap gap-1.5">
        <Link
          to={entityPath(row.entityType, row.entityId)}
          className={VERIFICATION_LINK_CLASS}
          data-testid={`${testId}-entity-link`}
        >
          {t('admin:verification.integrations.openEntity')}
        </Link>
        {row.openRequest ? (
          <Link
            to={requestPath(row.openRequest.requestId)}
            className={VERIFICATION_LINK_CLASS}
            data-testid={`${testId}-request-link`}
          >
            {t('admin:verification.common.openRequest')}
          </Link>
        ) : null}
      </div>
    </li>
  );
}

function FallbackRow({
  label,
  verified,
  entityType,
}: {
  readonly label: string;
  readonly verified: boolean | undefined;
  readonly entityType: 'player' | 'trainer';
}) {
  const { t } = useI18n();
  return (
    <li className="flex items-center gap-2 text-[13px] text-ink">
      <span>{label}</span>
      {verified === true ? (
        <InertTick verified entityType={entityType} />
      ) : (
        <span className="text-[12px] text-ink-3">{t('admin:verification.entity.notVerified')}</span>
      )}
    </li>
  );
}
